import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Star } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import { photoUrl } from "../../lib/usePortfolio";

const inputCls = "w-full bg-transparent border-b border-white/15 focus:border-champagne outline-none py-2.5 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors duration-300";
const labelCls = "text-[9px] font-mono tracking-[0.25em] uppercase text-stone-500";

export default function StoryEditor({ story, photos, onSaved, onCancel }) {
  const isNew = !story.id;
  const [form, setForm] = useState({
    title: story.title || "",
    couple: story.couple || "",
    venue: story.venue || "",
    date: story.date || "",
    excerpt: story.excerpt || "",
    body: story.body || "",
    cover_photo_id: story.cover_photo_id || null,
    photo_ids: story.photo_ids || [],
    published: story.published ?? false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const togglePhoto = (id) => {
    const has = form.photo_ids.includes(id);
    const photo_ids = has ? form.photo_ids.filter((x) => x !== id) : [...form.photo_ids, id];
    const cover_photo_id = has && form.cover_photo_id === id ? photo_ids[0] ?? null : form.cover_photo_id ?? (photo_ids.length ? photo_ids[0] : null);
    setForm({ ...form, photo_ids, cover_photo_id });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Give the story a title");
    setSaving(true);
    try {
      const { data } = isNew ? await adminApi.post("/journal", form) : await adminApi.patch(`/journal/${story.id}`, form);
      toast.success(isNew ? "Story created" : "Story saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="story-editor" className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5 space-y-6">
        <button type="button" data-testid="story-editor-back" onClick={onCancel} className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-400 hover:text-champagne transition-colors"><ArrowLeft size={12} /> Back to Journal</button>
        <h2 className="font-serif text-2xl text-ivory">{isNew ? "New" : "Edit"} <span className="italic text-champagne">Story</span></h2>
        <div><label className={labelCls} htmlFor="story-title">Title *</label><input id="story-title" data-testid="story-title-input" value={form.title} onChange={set("title")} className={inputCls} placeholder="A Candlelit Evening at Villa Balbiano" /></div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div><label className={labelCls} htmlFor="story-couple">Couple</label><input id="story-couple" data-testid="story-couple-input" value={form.couple} onChange={set("couple")} className={inputCls} placeholder="Ava & Noah" /></div>
          <div><label className={labelCls} htmlFor="story-venue">Venue</label><input id="story-venue" data-testid="story-venue-input" value={form.venue} onChange={set("venue")} className={inputCls} placeholder="Lake Como, Italy" /></div>
        </div>
        <div><label className={labelCls} htmlFor="story-date">Date (as shown)</label><input id="story-date" data-testid="story-date-input" value={form.date} onChange={set("date")} className={inputCls} placeholder="September 2026" /></div>
        <div><label className={labelCls} htmlFor="story-excerpt">Excerpt</label><textarea id="story-excerpt" data-testid="story-excerpt-input" rows={2} value={form.excerpt} onChange={set("excerpt")} className={`${inputCls} resize-none`} placeholder="One or two lines that set the scene…" /></div>
        <div><label className={labelCls} htmlFor="story-body">Story (blank line = new paragraph)</label><textarea id="story-body" data-testid="story-body-input" rows={12} value={form.body} onChange={set("body")} className="mt-1 w-full bg-surface border border-white/10 focus:border-champagne outline-none p-3 text-sm font-light text-ivory placeholder:text-stone-600 resize-y transition-colors" placeholder="The morning began with mist on the water…" /></div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="story-published-checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#E8D8C3] h-4 w-4" />
          <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-stone-300">Published (visible on the site)</span>
        </label>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" data-testid="story-save-button" disabled={saving} className="bg-champagne text-obsidian px-8 py-3.5 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors disabled:opacity-50">{saving ? "Saving…" : isNew ? "Create Story" : "Save Story"}</button>
          <button type="button" data-testid="story-cancel-button" onClick={onCancel} className="border border-white/15 px-6 py-3.5 text-[11px] font-mono tracking-[0.2em] uppercase text-stone-300 hover:border-champagne hover:text-champagne transition-colors">Cancel</button>
        </div>
      </div>

      <div className="lg:col-span-7">
        <p className={labelCls}>Frames from your portfolio — click to include, star to set the cover</p>
        <p className="mt-1 text-xs font-light text-stone-500">{form.photo_ids.length} selected{form.cover_photo_id ? " · cover set" : ""}</p>
        {photos.length === 0 ? (
          <p data-testid="story-no-photos" className="mt-6 font-serif italic text-stone-500">Upload photos in the Portfolio tab first, then return to build this story.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="story-photo-picker">
            {photos.map((p) => {
              const selected = form.photo_ids.includes(p.id);
              const isCover = form.cover_photo_id === p.id;
              return (
                <div key={p.id} className={`relative aspect-[4/3] overflow-hidden border transition-colors ${selected ? "border-champagne" : "border-white/10 hover:border-white/30"}`}>
                  <button type="button" data-testid={`story-pick-${p.id}`} onClick={() => togglePhoto(p.id)} className="absolute inset-0">
                    <img src={photoUrl(p)} alt={p.title} className={`h-full w-full object-cover transition-opacity ${selected ? "opacity-100" : "opacity-50 hover:opacity-80"}`} />
                    {selected && <span className="absolute top-1.5 left-1.5 bg-champagne text-obsidian p-1"><Check size={10} /></span>}
                  </button>
                  {selected && (
                    <button type="button" data-testid={`story-cover-${p.id}`} onClick={() => setForm({ ...form, cover_photo_id: p.id })} aria-label="Set as cover" className={`absolute top-1.5 right-1.5 p-1 ${isCover ? "bg-gold text-obsidian" : "bg-black/60 text-stone-300 hover:text-gold"}`}>
                      <Star size={10} />
                    </button>
                  )}
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1 text-[9px] font-mono text-stone-300 truncate">{p.title}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </form>
  );
}
